import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { PieChart as PieIcon } from 'lucide-react';
import { DashCard, DashTooltip, DASH_COLORS, compactMoney, money } from './DashCard';

/** Wide donut panel: donut · legend with share bars · headline figures. Reused on Summary. */
export const RevenueSplitCard = ({
  last30 = {},
  title = 'Revenue Split',
  subtitle = 'Where gross went · last 30 days',
  stats,
  testid = 'dashboard-revenue-split',
  delay = 200,
}) => {
  const data = [
    { name: 'Net to carriers', value: Math.max(last30.net || 0, 0), fill: DASH_COLORS.gross },
    { name: 'Dispatch fees', value: last30.fees || 0, fill: DASH_COLORS.fees },
    { name: 'Deductions', value: last30.deductions || 0, fill: DASH_COLORS.slate },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  const tiles = stats || [
    { label: 'Gross revenue', value: money(last30.gross) },
    { label: 'Loads', value: last30.loads ?? 0 },
    { label: 'Avg gross / load', value: money(last30.avg_per_load) },
  ];
  return (
    <DashCard title={title} subtitle={subtitle} icon={PieIcon} delay={delay} testid={testid}>
      {total === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-base text-slate-500 dark:text-slate-300">No revenue in this period</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] xl:grid-cols-[240px_1fr_260px] items-center gap-8">
          <div className="relative h-[240px] w-[240px] mx-auto">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={78} outerRadius={110} paddingAngle={3} cornerRadius={6} stroke="none" animationDuration={900}>
                  {data.map((d) => <Cell key={d.name} fill={d.fill} />)}
                </Pie>
                <Tooltip content={<DashTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-sm text-slate-600 dark:text-slate-300">Gross</span>
              <span className="font-mono text-2xl font-semibold text-slate-900 dark:text-white" data-testid={`${testid}-total`}>{compactMoney(last30.gross)}</span>
            </div>
          </div>
          <ul className="space-y-5 min-w-0">
            {data.map((d) => {
              const pct = total ? (d.value / total) * 100 : 0;
              return (
                <li key={d.name} data-testid={`${testid}-${d.name.split(' ')[0].toLowerCase()}`}>
                  <div className="flex items-center gap-3 text-[15px]">
                    <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: d.fill }} />
                    <span className="font-medium text-slate-800 dark:text-slate-100">{d.name}</span>
                    <span className="ml-auto font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">{money(d.value)}</span>
                    <span className="w-16 text-right font-semibold text-slate-900 dark:text-white">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="mt-2 ml-6 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full dash-bar" style={{ width: `${pct}%`, background: d.fill }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:col-span-2 xl:col-span-1 xl:grid-cols-1 gap-3">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-xl bg-slate-50 dark:bg-slate-800/60 px-4 py-3">
                <div className="text-sm text-slate-600 dark:text-slate-300">{t.label}</div>
                <div className="font-mono text-lg font-semibold text-slate-900 dark:text-white mt-0.5">{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashCard>
  );
};
